import type { AppDispatch } from "app/store";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";

import { fetchDrawingData } from "./fetchDrawingData";
import { setDrawingData, setDrawingPreview } from "./minimapSlice";
import { downloadTiledDrawingData, resolveTiledManifest, type TilingRequest } from "./tiledDrawing";
import type { Uuid } from "./types";

/**
 * Download a drawing by whichever route its platform uses (BM-13597,
 * BM-13878). The two are strictly either/or — there is no fallback between
 * them in production:
 *
 * `tiling` is the whole route decision (`buildTilingRequest`): present means
 * tiles, absent means PDF. With the host's `enableMinimapTiling` flag off it
 * is always absent, so this function behaves exactly as it did before the
 * tiled route existed.
 *
 *   1. **Tiled** — mobile only, whenever `tiling` is present. Fetches the
 *      ~1 KB manifest; pixels stream on demand. A failure here ends in
 *      `Error`, never in a PDF: rasterizing a PDF on the device is the exact
 *      memory/crash risk this route exists to avoid, so "no mini map" is the
 *      better outcome. The sheets that reach this point have already been
 *      filtered to those whose tile set is ready (`hasAvailableTileSet`, see
 *      `selectMiniMapDrawings`), so this is the backstop for a runtime
 *      failure — an expired signature, a network drop — not for a drawing
 *      that was never going to work.
 *   2. **PDF (legacy)** — pdf.js rasterization. Web's only route, and the
 *      only route anywhere when the host flag is off.
 *
 * PDF route notes: `fetchDrawingData` delivers a fast low-res preview
 * (`previewAsyncState`, drives the mini maps' UI) then slow full-res tiles
 * (`asyncState`, large-map only). Both go `Loading` up front; on failure the
 * tiles state goes `Error` but an already-delivered preview stays usable.
 * Intentionally no `AbortController`: an aborted fetch would strand the
 * states between `Loading` and a terminal status (see `useMiniMapData`).
 */
export async function downloadDrawingData({
    dispatch,
    drawingId,
    url,
    telemetrySource,
    tiling,
}: {
    dispatch: AppDispatch;
    drawingId: Uuid;
    url: string;
    telemetrySource: string;
    /** Tiled-route inputs (`buildTilingRequest`); `undefined` = pure PDF behavior. */
    tiling?: TilingRequest;
}): Promise<void> {
    dispatch(setDrawingPreview({ drawingId, asyncState: { status: AsyncStatus.Loading } }));
    dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Loading } }));

    if (tiling) {
        // Nothing in the tiled attempt may strand the states at `Loading`:
        // no call site attaches a `.catch`, so a throw anywhere in here is
        // treated exactly like a returned `tiledError`.
        let tiledError: Error | null = null;
        try {
            const manifest = await resolveTiledManifest(url, tiling);
            // `null` -> no tile set could be located at all.
            tiledError = manifest
                ? await downloadTiledDrawingData({ dispatch, drawingId, url, manifest, telemetrySource })
                : new Error("No tile set available for this drawing");
            if (!tiledError) {
                return;
            }
        } catch (error) {
            tiledError = error instanceof Error ? error : new Error(String(error));
            telemetry.trackCaughtException({
                source: telemetrySource,
                category: "render",
                error,
                severity: "warning",
                extraAttributes: { drawingId },
            });
        }
        // No PDF fallback: on mobile that is the crash risk tiling replaced,
        // and for a dev manifest override there is no PDF to fall back to.
        dispatch(setDrawingPreview({ drawingId, asyncState: { status: AsyncStatus.Error, msg: tiledError.message } }));
        dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Error, msg: tiledError.message } }));
        return;
    }

    let previewDelivered = false;

    return fetchDrawingData(url, {
        onPreview: (preview) => {
            previewDelivered = true;
            dispatch(setDrawingPreview({ drawingId, asyncState: { status: AsyncStatus.Success, data: preview } }));
        },
    })
        .then((image) => {
            dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Success, data: image } }));
        })
        .catch((error) => {
            const e = error as Error;
            telemetry.trackCaughtException({
                source: telemetrySource,
                category: "render",
                error,
                severity: "error",
                extraAttributes: { drawingId },
            });
            dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Error, msg: e.message } }));
            // Only fail the preview if it never arrived; a delivered preview stays
            // usable even when the background tiling fails.
            if (!previewDelivered) {
                dispatch(setDrawingPreview({ drawingId, asyncState: { status: AsyncStatus.Error, msg: e.message } }));
            }
        });
}
